import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users, Heart, Shield, Car, GraduationCap, Banknote, X, Check } from 'lucide-react';
import { hrService } from '../../services/hrService';
import { toast } from 'react-hot-toast';

const BenefitManagement = () => {
  const [benefits, setBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    coverage: '',
    provider: '',
    premium: 0,
    employer_contribution: 0,
    details: {},
    employee_ids: []
  });

  const categories = [
    { value: 'health', label: 'Health', icon: Heart },
    { value: 'insurance', label: 'Insurance', icon: Shield },
    { value: 'transportation', label: 'Transportation', icon: Car },
    { value: 'education', label: 'Education', icon: GraduationCap },
    { value: 'retirement', label: 'Retirement', icon: Banknote },
    { value: 'wellness', label: 'Wellness', icon: Heart },
    { value: 'other', label: 'Other', icon: Shield }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [benefitsRes, employeesRes] = await Promise.all([
        hrService.getBenefits(),
        hrService.getEmployees({ limit: 5000 })
      ]);
      setBenefits(benefitsRes?.data?.data?.benefits || []);
      setEmployees(employeesRes?.data?.data?.employees || employeesRes?.data?.employees || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBenefit) {
        await hrService.updateBenefit(editingBenefit.id, formData);
        toast.success('Benefit updated');
      } else {
        await hrService.createBenefit(formData);
        toast.success('Benefit created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save benefit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this benefit?')) return;
    try {
      await hrService.deleteBenefit(id);
      toast.success('Benefit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete benefit');
    }
  };

  const handleEdit = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      name: benefit.name,
      description: benefit.description || '',
      category: benefit.category,
      coverage: benefit.coverage || '',
      provider: benefit.provider || '',
      premium: benefit.premium || 0,
      employer_contribution: benefit.employer_contribution || 0,
      details: benefit.details || {},
      employee_ids: benefit.EmployeeBenefits?.map(eb => eb.employee_id) || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingBenefit(null);
    setFormData({
      name: '', description: '', category: 'other', coverage: '', provider: '',
      premium: 0, employer_contribution: 0, details: {}, employee_ids: []
    });
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId)
        ? prev.employee_ids.filter(id => id !== empId)
        : [...prev.employee_ids, empId]
    }));
  };

  const getCategoryIcon = (cat) => {
    const found = categories.find(c => c.value === cat);
    return found ? found.icon : Shield;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Benefits Management</h1>
          <p className="text-gray-600">Manage employee benefits and enrollments</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" /> Add Benefit
        </button>
      </div>

      {/* Benefits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {benefits.map(benefit => {
          const Icon = getCategoryIcon(benefit.category);
          const enrolledCount = benefit.EmployeeBenefits?.length || 0;
          return (
            <div key={benefit.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{benefit.name}</h3>
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded-full capitalize">{benefit.category}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(benefit)} className="p-1 text-gray-500 hover:text-blue-600"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(benefit.id)} className="p-1 text-gray-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{benefit.description}</p>
              <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                <div><span className="text-gray-500">Premium:</span> <span className="font-medium">₦{benefit.premium}</span></div>
                <div><span className="text-gray-500">Provider:</span> <span className="font-medium">{benefit.provider || 'N/A'}</span></div>
              </div>
              <div className="flex items-center gap-2 pt-4 border-t">
                <Users className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{enrolledCount} employees enrolled</span>
              </div>
            </div>
          );
        })}
      </div>

      {benefits.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg">
          <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No benefits configured yet</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">{editingBenefit ? 'Edit Benefit' : 'Add Benefit'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <input type="text" value={formData.provider} onChange={e => setFormData({...formData, provider: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Premium (₦)</label>
                  <input type="number" min="0" step="0.01" value={formData.premium} onChange={e => setFormData({...formData, premium: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employer Contribution (₦)</label>
                  <input type="number" min="0" step="0.01" value={formData.employer_contribution} onChange={e => setFormData({...formData, employer_contribution: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coverage</label>
                  <input type="text" value={formData.coverage} onChange={e => setFormData({...formData, coverage: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., Employee + Family" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={2} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Employees</label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {employees.length === 0 ? (
                    <p className="p-4 text-gray-500 text-sm">No employees found</p>
                  ) : (
                    employees.map(emp => (
                      <div key={emp.id} onClick={() => toggleEmployee(emp.id)}
                        className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 ${formData.employee_ids.includes(emp.id) ? 'bg-blue-50' : ''}`}>
                        <span className="text-sm">{emp.User?.first_name} {emp.User?.last_name}</span>
                        {formData.employee_ids.includes(emp.id) && <Check className="h-4 w-4 text-blue-600" />}
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{formData.employee_ids.length} employees selected</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingBenefit ? 'Update' : 'Create'} Benefit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitManagement;
